import React, { useEffect, useState } from 'react';
import { Card, Table, Spin, Modal, Alert } from 'antd';
import { AlertOutlined, XOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import * as VThunk from '../../slices/vehicles/thunk';
import { useDispatch, useSelector } from 'react-redux';
import './LastestStatus.css';

const LastestStatus = ({ style }) => {
  const dispatch = useDispatch();
  const { lastestStatusList } = useSelector(state => ({
    lastestStatusList: state.Vehicles.lastestStatusList || { latestStatus: [], latestAlarm: [] }
  }));

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alarmModalVisible, setAlarmModalVisible] = useState(false);
  const [alarmData, setAlarmData] = useState([]);

  // 将latestStatus数据转换为表格格式
  const transformStatusData = (data) => {
    return data.map(item => {
      return {
        key: item.id,
        vehicle_id: item.vehicle_id,
        licensePlate: item.vehicle_name,
        sn: item.sn,
        windSpeed: item.backup_1 ? (parseInt(item.backup_1) / 10).toFixed(1) : '0.0',
        moment: item.backup_2 ? (parseInt(item.backup_2) / 10).toFixed(1) : '0.0',
        weight: item.backup_3 ? (parseInt(item.backup_3) / 10).toFixed(1) : '0.0',
        boomLength: item.backup_4 ? (parseInt(item.backup_4) / 10).toFixed(1) : '0.0',
        angle: item.backup_5 ? (parseInt(item.backup_5) / 10).toFixed(1) : '0.0',
        rotateAngle: item.backup_6 ? (parseInt(item.backup_6) / 10).toFixed(1) : '0.0',
      };
    });
  };

  // 转换报警数据格式
  const transformAlarmData = (alarmList) => {
    return alarmList.map((item, index) => {
      const limitType = item.d === 'left' ? '左侵限' : '右侵限';
      return {
        key: index,
        vehicle_id: item.vehicle_id,
        vehicle_sn: item.vehicle_sn,
        licensePlate: item.vehicle_name,
        limitType: limitType,
        exceededDegree: Math.abs(parseFloat(item.py) || 0).toFixed(2),
        baseAngle: item.jz,
        currentAngle: item.dq,
        offset: item.py,
        leftThreshold: item.zyz,
        rightThreshold: item.yyz,
      };
    });
  };

  // 获取数据
  const fetchData = async () => {
    setLoading(true);
    try {
      await dispatch(VThunk.getLastestStatus());
    } finally {
      setLoading(false);
    }
  };

  // 初始加载
  useEffect(() => {
    // 只有当 lastestStatusList 为空时才调用 API，避免页面刷新重复查询
    if (!lastestStatusList || !lastestStatusList.latestStatus || lastestStatusList.latestStatus.length === 0) {
      fetchData();
    }
  }, [dispatch]);

  // 定时刷新
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 60000);
    return () => clearInterval(interval);
  }, [dispatch]);

  // 更新表格和报警
  useEffect(() => {
    const { latestStatus = [], latestAlarm = [] } = lastestStatusList;

    // 更新表格数据
    const transformedStatus = transformStatusData(latestStatus);
    setTableData(transformedStatus);

    // 更新报警数据
    const transformedAlarm = transformAlarmData(latestAlarm);
    if (transformedAlarm.length > 0) {
      setAlarmData(transformedAlarm);
      setAlarmModalVisible(true);
    }
  }, [lastestStatusList]);

  // 关闭报警
  const handleCloseAlarm = () => {
    setAlarmModalVisible(false);
  };

  // 表格列定义
  const columns = [
    {
      title: '车牌号',
      dataIndex: 'licensePlate',
      key: 'licensePlate',
      width: 200,
      align: 'center',
      render: (text, record) => <Link to={`/vehicles/${record.vehicle_id}?tab=location`}>{text}</Link>,
    },
    { title: '风速m/s', dataIndex: 'windSpeed', key: 'windSpeed', width: 80, align: 'center' },
    { title: '力矩nm', dataIndex: 'moment', key: 'moment', width: 100, align: 'center' },
    { title: '重量kg', dataIndex: 'weight', key: 'weight', width: 80, align: 'center' },
    { title: '臂长m', dataIndex: 'boomLength', key: 'boomLength', width: 80, align: 'center' },
    { title: '角度°', dataIndex: 'angle', key: 'angle', width: 80, align: 'center' },
    { title: '旋转角度°', dataIndex: 'rotateAngle', key: 'rotateAngle', width: 80, align: 'center' },
  ];

  return (
    <>
      <Card title="车辆车身状态实时数据" className="compact-card" style={style} >
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={tableData}
            size="small"
            pagination={{ pageSize: 3, size: 'small' }}
            scroll={{ x: true }}
            className="compact-table table-list"
          />
        </Spin>
      </Card>

      {/* 报警弹窗 */}
      <Modal
        open={alarmModalVisible}
        onCancel={handleCloseAlarm}
        footer={null}
        closable={false}
        width={480}
        wrapClassName="alarm-modal-wrap"
      >
        <div className="alarm-modal-content">
          <div className="alarm-header">
            <div className="alarm-icon-wrapper">
              <div className="alarm-icon">
                <AlertOutlined />
              </div>
              <div>
                <h2 className="alarm-title">侵限报警</h2>
                <p className="alarm-subtitle">检测到车辆侵限，请立即处理</p>
              </div>
            </div>
            <button className="alarm-close-btn" onClick={handleCloseAlarm}>
              <XOutlined />
            </button>
          </div>

          <div className="alarm-list">
            {alarmData.map((item, index) => (
              <Alert
                key={index}
                message={
                  <div>
                    <div className="alarm-item-plate">车牌: <Link to={`/vehicles/${item.vehicle_id}?tab=location`}>{item.licensePlate}</Link></div>
                    <div className="alarm-item-info">{item.limitType} | 基准角度: {item.baseAngle}° | 当前角度: {item.currentAngle}° | 偏移量: {item.offset}° | 左阈值: {item.leftThreshold}° | 右阈值: {item.rightThreshold}°</div>
                  </div>
                }
                type="error"
                showIcon={false}
                className="alarm-item"
              />
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default LastestStatus;